import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { getAbout, adminUpdateAbout } from '../api/api';

export const AdminAbout = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: ''
  });

  useEffect(() => {
    fetchAbout();
  }, []);

  const fetchAbout = async () => {
    try {
      const data = await getAbout();
      setFormData({
        title: data.title,
        content: data.content
      });
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      await adminUpdateAbout(formData);
      toast.success('Contenu mis à jour avec succès');
      fetchAbout();
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Contenu "À propos"</h2>
        <p className="text-gray-600">Modifiez le contenu de la section "À propos"</p>
      </div>

      <Card className="border-0 shadow-lg">
        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="title">Titre de la section *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                placeholder="À propos de NETTO+"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="content">Contenu *</Label>
              <Textarea
                id="content"
                value={formData.content}
                onChange={(e) => setFormData(prev => ({ ...prev, content: e.target.value }))}
                required
                rows={15}
                className="font-mono text-sm"
                placeholder="Séparez les paragraphes par une ligne vide (double saut de ligne)..."
              />
              <p className="text-sm text-gray-500">
                Astuce : pour créer plusieurs paragraphes, laissez une ligne vide entre eux
              </p>
            </div>

            <div className="flex justify-end pt-4">
              <Button
                type="submit"
                disabled={saving}
                className="bg-blue-600 hover:bg-blue-700 px-8"
              >
                {saving ? (
                  <span className="flex items-center">
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sauvegarde...
                  </span>
                ) : (
                  <span className="flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    Sauvegarder les modifications
                  </span>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Preview Section */}
      <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
        <CardContent className="p-8">
          <h3 className="text-lg font-bold text-gray-900 mb-6">Aperçu</h3>
          <div className="prose max-w-none">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">{formData.title}</h2>
            {formData.content.split('\n\n').map((paragraph, index) => (
              <p key={index} className="text-gray-700 leading-relaxed mb-4">
                {paragraph}
              </p>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
